import { z } from 'zod';
import { DecimalString, IsoDateTime, UuidString } from '../primitives.js';

/**
 * S18 — Project Task (tree via parent_task_id + DAG via project_task_depends_on).
 * ERPNext task.json parity, минус `Template` status.
 */

export const ProjectTaskStatus = z.enum([
  'open',
  'working',
  'pending_review',
  'overdue',
  'completed',
  'cancelled',
]);
export type ProjectTaskStatus = z.infer<typeof ProjectTaskStatus>;

export const PROJECT_TASK_STATUS_LABEL: Record<ProjectTaskStatus, string> = {
  open: 'Открыта',
  working: 'В работе',
  pending_review: 'На проверке',
  overdue: 'Просрочена',
  completed: 'Завершена',
  cancelled: 'Отменена',
};

export const ProjectTask = z
  .object({
    id: UuidString,
    entity_id: UuidString,
    project_id: UuidString,
    parent_task_id: UuidString.nullable(),
    position: z.number().int().nonnegative(),
    subject: z.string().min(1).max(255),
    description: z.string().nullable(),
    status: ProjectTaskStatus.default('open'),
    is_milestone: z.boolean().default(false),
    is_group: z.boolean().default(false),
    exp_start_date: IsoDateTime.nullable(),
    exp_end_date: IsoDateTime.nullable(),
    /** Computed via P8. */
    act_start_date: IsoDateTime.nullable(),
    act_end_date: IsoDateTime.nullable(),
    expected_time_hours: DecimalString.default('0'),
    /** Computed sum task_logs.hours. */
    actual_time_hours: DecimalString.default('0'),
    progress: DecimalString.default('0'),
    task_weight: DecimalString.default('1'),
    assignee_user_id: UuidString.nullable(),
    assignee_name: z.string().nullable().optional(),
    milestone_id: UuidString.nullable(),
    milestone_name: z.string().nullable().optional(),
    /** Computed enrichment — count deps where this task depends_on others. */
    dependency_count: z.number().int().nonnegative().optional(),
    created_at: IsoDateTime,
    updated_at: IsoDateTime,
  })
  .passthrough();
export type ProjectTask = z.infer<typeof ProjectTask>;

/** Tree node for ?mode=tree responses. */
export type ProjectTaskTreeNode = ProjectTask & { children: ProjectTaskTreeNode[] };

export const ProjectTaskDependsOn = z
  .object({
    task_id: UuidString,
    depends_on_task_id: UuidString,
    created_at: IsoDateTime,
  })
  .passthrough();
export type ProjectTaskDependsOn = z.infer<typeof ProjectTaskDependsOn>;

const ProjectTaskUpsertInputBase = z.object({
  subject: z.string().min(1).max(255),
  description: z.string().nullable().optional(),
  status: ProjectTaskStatus.optional(),
  is_milestone: z.boolean().optional(),
  is_group: z.boolean().optional(),
  parent_task_id: UuidString.nullable().optional(),
  exp_start_date: IsoDateTime.nullable().optional(),
  exp_end_date: IsoDateTime.nullable().optional(),
  expected_time_hours: DecimalString.optional(),
  progress: DecimalString.optional(),
  task_weight: DecimalString.optional(),
  assignee_user_id: UuidString.nullable().optional(),
  milestone_id: UuidString.nullable().optional(),
  /** Inline diff for depends_on update — full target state. */
  depends_on_task_ids: z.array(UuidString).optional(),
});

export const ProjectTaskUpsertInput = ProjectTaskUpsertInputBase.refine(
  (d) => !(d.is_group === true && d.expected_time_hours && d.expected_time_hours !== '0'),
  { message: 'task_group_cannot_have_hours', path: ['expected_time_hours'] },
);
export type ProjectTaskUpsertInput = z.infer<typeof ProjectTaskUpsertInput>;

/** Partial для PUT — derived from base (refine sheds partial method on ZodEffects). */
export const ProjectTaskUpdateInput = ProjectTaskUpsertInputBase.partial();
export type ProjectTaskUpdateInput = z.infer<typeof ProjectTaskUpdateInput>;

export const ProjectTaskReorderInput = z.object({
  new_parent_task_id: UuidString.nullable().optional(),
  new_position: z.number().int().nonnegative(),
});
export type ProjectTaskReorderInput = z.infer<typeof ProjectTaskReorderInput>;

export const ProjectTaskStatusInput = z.object({ new_status: ProjectTaskStatus });
export type ProjectTaskStatusInput = z.infer<typeof ProjectTaskStatusInput>;
